import { zodResolver } from '@hookform/resolvers/zod';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type UseFormReturn, useFieldArray, useForm } from 'react-hook-form';

import {
  type ClassModuleDisciplineForm,
  type ClassModuleFormValues,
  classModuleFormSchema,
  type LinkedClassForm,
} from '../types/classModuleForm';

const EMPTY_FORM: ClassModuleFormValues = {
  companyId: null,
  modality: null,
  description: '',
  dateStart: '',
  dateFinish: '',
  linkedClasses: [],
  disciplines: [],
};

/** Item pendente de confirmação de exclusão (turma ou disciplina). */
export type PendingDelete =
  | { readonly kind: 'class'; readonly index: number; readonly name: string }
  | { readonly kind: 'discipline'; readonly index: number; readonly name: string };

/** Valor exposto pelo contexto do formulário de módulo. */
export interface ClassModuleFormContextValue {
  readonly form: UseFormReturn<ClassModuleFormValues>;
  readonly isEdit: boolean;

  readonly linkedClasses: readonly LinkedClassForm[];
  readonly addLinkedClass: (item: LinkedClassForm) => boolean;
  readonly requestRemoveLinkedClass: (index: number) => void;

  readonly disciplines: readonly ClassModuleDisciplineForm[];
  readonly saveDiscipline: (item: ClassModuleDisciplineForm, index: number | null) => void;
  readonly requestRemoveDiscipline: (index: number) => void;

  readonly expandedDisciplineIndex: number | null;
  readonly toggleDiscipline: (index: number) => void;

  readonly disciplineModalIndex: number | null;
  readonly isDisciplineModalOpen: boolean;
  readonly openAddDiscipline: () => void;
  readonly openEditDiscipline: (index: number) => void;
  readonly closeDisciplineModal: () => void;

  readonly pendingDelete: PendingDelete | null;
  readonly confirmDelete: () => void;
  readonly cancelDelete: () => void;
}

const ClassModuleFormContext = createContext<ClassModuleFormContextValue | null>(null);
ClassModuleFormContext.displayName = 'ClassModuleFormContext';

/**
 * Hook de acesso ao contexto do formulário de módulo.
 *
 * @throws Se usado fora de `<ClassModuleFormProvider>`.
 */
export function useClassModuleForm(): ClassModuleFormContextValue {
  const ctx = useContext(ClassModuleFormContext);
  if (!ctx) {
    throw new Error('useClassModuleForm must be used within <ClassModuleFormProvider>');
  }
  return ctx;
}

export interface ClassModuleFormProviderProps {
  /** Valores iniciais (modo edição); ausente/`undefined` no cadastro. */
  readonly initialValues?: ClassModuleFormValues;
  readonly isEdit: boolean;
}

/**
 * Provider do formulário de módulo. Concentra o React Hook Form (com field
 * arrays de turmas e disciplinas), o estado de UI efêmero (aba, expansão, modal
 * de disciplina, exclusão pendente) e os helpers de manipulação, mantendo os
 * organisms desacoplados da mecânica do formulário.
 */
export function ClassModuleFormProvider({
  initialValues,
  isEdit,
  children,
}: PropsWithChildren<ClassModuleFormProviderProps>) {
  // Monta o formulário já preenchido no modo edição (a página só renderiza este
  // provider após o detalhe carregar). Assim os campos controlados — em especial
  // o DatePicker — inicializam com o valor real desde o primeiro render, sem
  // depender do `reset` posterior.
  const form = useForm<ClassModuleFormValues>({
    resolver: zodResolver(classModuleFormSchema),
    defaultValues: initialValues ?? EMPTY_FORM,
  });

  // Reaplica caso `initialValues` chegue/mude depois da montagem.
  useEffect(() => {
    if (initialValues) form.reset(initialValues);
  }, [initialValues, form]);

  const linkedClassesArray = useFieldArray({ control: form.control, name: 'linkedClasses' });
  const disciplinesArray = useFieldArray({ control: form.control, name: 'disciplines' });

  const [expandedDisciplineIndex, setExpandedDisciplineIndex] = useState<number | null>(null);
  const [disciplineModalIndex, setDisciplineModalIndex] = useState<number | null>(null);
  const [isDisciplineModalOpen, setDisciplineModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const linkedClasses = form.watch('linkedClasses');
  const disciplines = form.watch('disciplines');

  const addLinkedClass = useCallback(
    (item: LinkedClassForm): boolean => {
      const exists = form
        .getValues('linkedClasses')
        .some(c => c.classStudentId === item.classStudentId);
      if (exists) return false;
      linkedClassesArray.append(item);
      return true;
    },
    [form, linkedClassesArray],
  );

  const requestRemoveLinkedClass = useCallback(
    (index: number) => {
      const item = form.getValues('linkedClasses')[index];
      setPendingDelete({ kind: 'class', index, name: item?.name ?? '' });
    },
    [form],
  );

  const saveDiscipline = useCallback(
    (item: ClassModuleDisciplineForm, index: number | null) => {
      if (index === null) {
        disciplinesArray.append(item);
      } else {
        disciplinesArray.update(index, item);
      }
      setDisciplineModalOpen(false);
      setDisciplineModalIndex(null);
    },
    [disciplinesArray],
  );

  const requestRemoveDiscipline = useCallback(
    (index: number) => {
      const item = form.getValues('disciplines')[index];
      setPendingDelete({ kind: 'discipline', index, name: item?.disciplineName ?? '' });
    },
    [form],
  );

  const toggleDiscipline = useCallback((index: number) => {
    setExpandedDisciplineIndex(prev => (prev === index ? null : index));
  }, []);

  const openAddDiscipline = useCallback(() => {
    setDisciplineModalIndex(null);
    setDisciplineModalOpen(true);
  }, []);

  const openEditDiscipline = useCallback((index: number) => {
    setDisciplineModalIndex(index);
    setDisciplineModalOpen(true);
  }, []);

  const closeDisciplineModal = useCallback(() => {
    setDisciplineModalOpen(false);
    setDisciplineModalIndex(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === 'class') {
      linkedClassesArray.remove(pendingDelete.index);
    } else {
      disciplinesArray.remove(pendingDelete.index);
    }
    setPendingDelete(null);
  }, [pendingDelete, linkedClassesArray, disciplinesArray]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const value = useMemo<ClassModuleFormContextValue>(
    () => ({
      form,
      isEdit,
      linkedClasses,
      addLinkedClass,
      requestRemoveLinkedClass,
      disciplines,
      saveDiscipline,
      requestRemoveDiscipline,
      expandedDisciplineIndex,
      toggleDiscipline,
      disciplineModalIndex,
      isDisciplineModalOpen,
      openAddDiscipline,
      openEditDiscipline,
      closeDisciplineModal,
      pendingDelete,
      confirmDelete,
      cancelDelete,
    }),
    [
      form,
      isEdit,
      linkedClasses,
      addLinkedClass,
      requestRemoveLinkedClass,
      disciplines,
      saveDiscipline,
      requestRemoveDiscipline,
      expandedDisciplineIndex,
      toggleDiscipline,
      disciplineModalIndex,
      isDisciplineModalOpen,
      openAddDiscipline,
      openEditDiscipline,
      closeDisciplineModal,
      pendingDelete,
      confirmDelete,
      cancelDelete,
    ],
  );

  return (
    <ClassModuleFormContext.Provider value={value}>{children}</ClassModuleFormContext.Provider>
  );
}
